import { useState, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import axios from "axios";
import "@/App.css";
import ErrorBoundary from "@/components/ErrorBoundary";
import { captureAttribution } from "@/utils/utm";
import { initAnalytics } from "@/utils/analytics";
import LandingPage from "@/pages/LandingPage";
import LaunchLandingPage from "@/pages/LaunchLandingPage";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import MagicLinkPage from "@/pages/MagicLinkPage";
import DeleteAccountPage from "@/pages/DeleteAccountPage";
import PrivacyPolicyPage from "@/pages/PrivacyPolicyPage";
import TermsPage from "@/pages/TermsPage";
import StudentPrivacyPage from "@/pages/StudentPrivacyPage";
import SubprocessorsPage from "@/pages/SubprocessorsPage";
import SecurityAndStudentDataPage from "@/pages/SecurityAndStudentDataPage";
import DPAPage from "@/pages/DPAPage";
import CookieBanner from "@/components/CookieBanner";
import StudentDashboard from "@/pages/StudentDashboard";
import MiddleSchoolDashboard from "@/pages/MiddleSchoolDashboard";
import MiddleSchoolLessonView from "@/pages/MiddleSchoolLessonView";
import EducatorDashboard from "@/pages/EducatorDashboard";
import AdminDashboard from "@/pages/AdminDashboard";
import AdminAnnouncementsPage from "@/pages/AdminAnnouncementsPage";
import AdminLeadsPage from "@/pages/AdminLeadsPage";
import AdminDemoReportsPage from "@/pages/AdminDemoReportsPage";
import LessonView from "@/pages/LessonView";
import ClassView from "@/pages/ClassView";
import FeedbackDashboard from "@/pages/FeedbackDashboard";
import ProfilePage from "@/pages/ProfilePage";
import CompletionPage from "@/pages/CompletionPage";
import BonusResources from "@/pages/BonusResources";
import AssessmentPage from "@/pages/AssessmentPage";
import EducatorAssessmentDashboard from "@/pages/EducatorAssessmentDashboard";
import BusinessPlanPage from "@/pages/BusinessPlanPage"; // Now renamed to Workbook internally
import SharedBusinessPlanPage from "@/pages/SharedBusinessPlanPage";
import AIBusinessPlanPage from "@/pages/AIBusinessPlanPage";
import PitchPracticePage from "@/pages/PitchPracticePage";
import StudentPreviewPage from "@/pages/StudentPreviewPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import ParentAccessPage from "@/pages/ParentAccessPage";
import CertificateVerifyPage from "@/pages/CertificateVerifyPage";
import AnalyticsDashboard from "@/pages/AnalyticsDashboard";
import GameHostPage from "@/pages/GameHostPage";
import GameJoinPage from "@/pages/GameJoinPage";
import GamePlayPage from "@/pages/GamePlayPage";
import GameDemoPage from "@/pages/GameDemoPage";
import GameSoloPage from "@/pages/GameSoloPage";
import GameProfilePage from "@/pages/GameProfilePage";
import TeacherReportsPage from "@/pages/TeacherReportsPage";
import TeacherMetrics from "@/pages/TeacherMetrics";
import DistrictSnapshot from "@/pages/DistrictSnapshot";
import PublicJourneyMap from "@/pages/PublicJourneyMap";
import PublicClassInsights from "@/pages/PublicClassInsights";
import PublicDistrictDashboard from "@/pages/PublicDistrictDashboard";
import ViewAsStudentPage from "@/pages/ViewAsStudentPage";
import CompareClassesPage from "@/pages/CompareClassesPage";
import ClassSettingsPage from "@/pages/ClassSettingsPage";
import ElementaryLanding from "@/pages/elementary/ElementaryLanding";
import ElementaryDashboard from "@/pages/elementary/ElementaryDashboard";
import ElementaryPublicPreview from "@/pages/elementary/ElementaryPublicPreview";
import ElementaryCertificatePreview from "@/pages/elementary/ElementaryCertificatePreview";
import CEOChallengeStart from "@/pages/ceo-challenge/CEOChallengeStart";
import CEOChallengePlay from "@/pages/ceo-challenge/CEOChallengePlay";
import CEOChallengeResults from "@/pages/ceo-challenge/CEOChallengeResults";
import CEOChallengeLeaderboard from "@/pages/ceo-challenge/CEOChallengeLeaderboard";
import AdminCEOChallenge from "@/pages/ceo-challenge/AdminCEOChallenge";
import Mission1 from "@/pages/elementary/Mission1";
import Mission2 from "@/pages/elementary/Mission2";
import Mission3 from "@/pages/elementary/Mission3";
import Mission4 from "@/pages/elementary/Mission4";
import Mission8 from "@/pages/elementary/Mission8";
import Mission5 from "@/pages/elementary/Mission5";
import Mission6 from "@/pages/elementary/Mission6";
import Mission7 from "@/pages/elementary/Mission7";
import Mission9 from "@/pages/elementary/Mission9";
import Footer from "@/components/Footer";
import { AccessibilityProvider } from "@/contexts/AccessibilityContext";
import AccessibilityFloatingButton from "@/components/accessibility/AccessibilityFloatingButton";

// Alias for clarity - BusinessPlanPage is now the Workbook (reflections)
const WorkbookPage = BusinessPlanPage;

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Set up axios defaults
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

function AppShell() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const token = localStorage.getItem("token");
    if (token) {
      try {
        const response = await axios.get(`${API}/auth/me`);
        setUser(response.data);
      } catch (error) {
        console.error("Auth check failed:", error);
        localStorage.removeItem("token");
      }
    }
    setLoading(false);
  };

  const handleLogin = (userData, token) => {
    localStorage.setItem("token", token);
    setUser(userData);
  };

  const handleLogout = () => {
    // Clear all authentication tokens
    localStorage.removeItem("token");
    localStorage.removeItem("lemonade_student_token");
    localStorage.removeItem("lemonade_student_user");
    setUser(null);
  };

  const handleUserUpdate = (updatedUser) => {
    setUser(updatedUser);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-yellow-50 to-orange-50">
        <div className="text-center">
          <div className="text-6xl mb-4">🍋</div>
          <div className="text-xl text-gray-600">Loading...</div>
        </div>
      </div>
    );
  }

  const ProtectedRoute = ({ children, allowedRoles }) => {
    if (!user) {
      return <Navigate to="/login" />;
    }
    if (allowedRoles && !allowedRoles.includes(user.role)) {
      return <Navigate to="/" />;
    }
    return children;
  };

  // Scroll to top on route change
  const ScrollToTop = () => {
    const { pathname } = useLocation();
    
    useEffect(() => {
      // Immediate scroll
      window.scrollTo(0, 0);
      
      // Delayed scroll to override any component-level scrolling
      const timer = setTimeout(() => {
        window.scrollTo(0, 0);
      }, 100);
      
      return () => clearTimeout(timer);
    }, [pathname]);
    
    return null;
  };

  return (
    <BrowserRouter>
      <ScrollToTop />
      <CookieBanner />
      <Routes>
        <Route path="/" element={user ? <Navigate to="/dashboard" /> : <LaunchLandingPage />} />
        <Route path="/old-landing" element={<LandingPage />} />
        <Route path="/privacy" element={<PrivacyPolicyPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/student-privacy" element={<StudentPrivacyPage />} />
        <Route path="/subprocessors" element={<SubprocessorsPage />} />
        <Route path="/security-and-student-data" element={<SecurityAndStudentDataPage />} />
        <Route path="/dpa" element={<DPAPage />} />
        <Route path="/login" element={user ? <Navigate to="/dashboard" /> : <LoginPage onLogin={handleLogin} />} />
        <Route path="/register" element={user ? <Navigate to="/dashboard" /> : <RegisterPage onLogin={handleLogin} />} />
        <Route path="/forgot-password" element={user ? <Navigate to="/dashboard" /> : <ForgotPasswordPage />} />
        <Route path="/reset-password" element={user ? <Navigate to="/dashboard" /> : <ResetPasswordPage />} />
        
        {/* Magic Link verification route */}
        <Route path="/auth/magic-link/:token" element={<MagicLinkPage onLogin={handleLogin} />} />
        
        {/* Delete Account route - accessible with or without login */}
        <Route path="/delete-account" element={<DeleteAccountPage user={user} onLogout={handleLogout} />} />
        
        {/* Public route for shared business plans */}
        <Route path="/shared-plan/:shareToken" element={<SharedBusinessPlanPage />} />
        <Route path="/snapshot/:token" element={<DistrictSnapshot />} />
        <Route path="/journey/:token" element={<PublicJourneyMap />} />
        <Route path="/insights/:token" element={<PublicClassInsights />} />
        <Route path="/district/:token" element={<PublicDistrictDashboard />} />
        
        {/* Public route for parent access */}
        <Route path="/parent/:shareToken" element={<ParentAccessPage />} />
        <Route path="/verify/:certId" element={<CertificateVerifyPage />} />

        {/* Lemonade Leaders Jr. (Elementary pathway — public, no auth) */}
        <Route path="/lemonade-leaders-jr" element={<ElementaryLanding />} />
        <Route path="/elementary" element={<Navigate to="/lemonade-leaders-jr" replace />} />
        <Route path="/elementary/demo" element={<ElementaryDashboard />} />
        <Route path="/elementary/demo/mission/1" element={<Mission1 />} />
        <Route path="/elementary/demo/mission/2" element={<Mission2 />} />
        <Route path="/elementary/demo/mission/3" element={<Mission3 />} />
        <Route path="/elementary/demo/mission/4" element={<Mission4 />} />
        <Route path="/elementary/demo/mission/5" element={<Mission5 />} />
        <Route path="/elementary/demo/mission/6" element={<Mission6 />} />
        <Route path="/elementary/demo/mission/7" element={<Mission7 />} />
        <Route path="/elementary/demo/mission/8" element={<Mission8 />} />
        <Route path="/elementary/demo/mission/9" element={<Mission9 />} />
        <Route path="/elementary/demo/certificate-preview" element={<ElementaryCertificatePreview />} />
        <Route path="/share/elementary-preview" element={<ElementaryPublicPreview />} />
        
        {/* Live Game Routes */}
        <Route path="/game/join" element={<GameJoinPage />} />
        <Route path="/game/play" element={<GamePlayPage />} />
        <Route
          path="/game/host"
          element={
            <ProtectedRoute allowedRoles={["educator", "admin"]}>
              <GameHostPage user={user} onLogout={handleLogout} />
            </ProtectedRoute>
          }
        />
        <Route
          path="/game/demo"
          element={
            <ProtectedRoute allowedRoles={["educator", "admin"]}>
              <GameDemoPage user={user} onLogout={handleLogout} />
            </ProtectedRoute>
          }
        />
        <Route
          path="/game/solo"
          element={
            <ProtectedRoute allowedRoles={["student"]}>
              <GameSoloPage user={user} onLogout={handleLogout} />
            </ProtectedRoute>
          }
        />
        <Route
          path="/game/profile"
          element={
            <ProtectedRoute allowedRoles={["student"]}>
              <GameProfilePage user={user} onLogout={handleLogout} />
            </ProtectedRoute>
          }
        />
        <Route
          path="/game/reports"
          element={
            <ProtectedRoute allowedRoles={["educator", "admin"]}>
              <TeacherReportsPage user={user} onLogout={handleLogout} />
            </ProtectedRoute>
          }
        />
        <Route
          path="/game/reports/:classId"
          element={
            <ProtectedRoute allowedRoles={["educator", "admin"]}>
              <TeacherReportsPage user={user} onLogout={handleLogout} />
            </ProtectedRoute>
          }
        />
        <Route
          path="/game/compare-classes"
          element={
            <ProtectedRoute allowedRoles={["educator", "admin"]}>
              <CompareClassesPage user={user} onLogout={handleLogout} />
            </ProtectedRoute>
          }
        />
        
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <ErrorBoundary scope="dashboard">
                {user?.role === "student" && (
                  (user?.learning_path === "middle_school" || localStorage.getItem("preferred_learning_path") === "middle_school")
                    ? <Navigate to="/middle-school" replace />
                    : <StudentDashboard user={user} onLogout={handleLogout} />
                )}
                {user?.role === "educator" && <EducatorDashboard user={user} onLogout={handleLogout} />}
                {user?.role === "admin" && <AdminDashboard user={user} onLogout={handleLogout} />}
              </ErrorBoundary>
            </ProtectedRoute>
          }
        />
        
        <Route
          path="/educator/cte-metrics"
          element={
            <ProtectedRoute allowedRoles={["educator", "admin"]}>
              <TeacherMetrics user={user} onLogout={handleLogout} />
            </ProtectedRoute>
          }
        />

        <Route
          path="/middle-school"
          element={
            <ProtectedRoute>
              <ErrorBoundary scope="dashboard">
                <MiddleSchoolDashboard user={user} onLogout={handleLogout} />
              </ErrorBoundary>
            </ProtectedRoute>
          }
        />
        
        <Route
          path="/middle-school/lessons/:lessonId"
          element={
            <ProtectedRoute>
              <ErrorBoundary scope="lesson">
                <MiddleSchoolLessonView user={user} onLogout={handleLogout} />
              </ErrorBoundary>
            </ProtectedRoute>
          }
        />
        
        <Route
          path="/completion"
          element={
            <ProtectedRoute allowedRoles={["student"]}>
              <CompletionPage user={user} onLogout={handleLogout} />
            </ProtectedRoute>
          }
        />
        
        <Route
          path="/bonus"
          element={
            <ProtectedRoute allowedRoles={["student"]}>
              <BonusResources user={user} onLogout={handleLogout} />
            </ProtectedRoute>
          }
        />
        
        <Route
          path="/assessment/:moduleId"
          element={
            <ProtectedRoute allowedRoles={["student"]}>
              <AssessmentPage user={user} onLogout={handleLogout} />
            </ProtectedRoute>
          }
        />
        
        <Route
          path="/lessons/:lessonId"
          element={
            <ProtectedRoute>
              <ErrorBoundary scope="lesson">
                <LessonView user={user} onLogout={handleLogout} />
              </ErrorBoundary>
            </ProtectedRoute>
          }
        />
        
        <Route
          path="/classes/:classId"
          element={
            <ProtectedRoute>
              <ClassView user={user} onLogout={handleLogout} />
            </ProtectedRoute>
          }
        />
        
        <Route
          path="/classes/:classId/assessments"
          element={
            <ProtectedRoute allowedRoles={["educator", "admin"]}>
              <EducatorAssessmentDashboard user={user} onLogout={handleLogout} />
            </ProtectedRoute>
          }
        />
        
        <Route
          path="/feedback"
          element={
            <ProtectedRoute allowedRoles={["educator"]}>
              <FeedbackDashboard user={user} onLogout={handleLogout} />
            </ProtectedRoute>
          }
        />
        
        <Route
          path="/class/:classId/settings"
          element={
            <ProtectedRoute allowedRoles={["educator"]}>
              <ClassSettingsPage user={user} onLogout={handleLogout} />
            </ProtectedRoute>
          }
        />
        
        <Route
          path="/analytics"
          element={
            <ProtectedRoute allowedRoles={["educator", "admin"]}>
              <AnalyticsDashboard user={user} onLogout={handleLogout} />
            </ProtectedRoute>
          }
        />
        
        <Route
          path="/profile"
          element={
            <ProtectedRoute>
              <ProfilePage user={user} onLogout={handleLogout} onUserUpdate={handleUserUpdate} />
            </ProtectedRoute>
          }
        />
        
        {/* Workbook (Student Reflections) Routes */}
        <Route
          path="/workbook"
          element={
            <ProtectedRoute allowedRoles={["student"]}>
              <WorkbookPage user={user} onLogout={handleLogout} />
            </ProtectedRoute>
          }
        />
        
        <Route
          path="/workbook/:studentId"
          element={
            <ProtectedRoute allowedRoles={["educator", "admin"]}>
              <WorkbookPage user={user} onLogout={handleLogout} />
            </ProtectedRoute>
          }
        />
        
        {/* Legacy routes - redirect to workbook */}
        <Route
          path="/business-plan"
          element={<Navigate to="/workbook" replace />}
        />
        
        <Route
          path="/business-plan/:studentId"
          element={<Navigate to="/workbook/:studentId" replace />}
        />
        
        {/* Business Plan (AI-Generated) Routes */}
        <Route
          path="/ai-business-plan"
          element={
            <ProtectedRoute allowedRoles={["student"]}>
              <AIBusinessPlanPage user={user} onLogout={handleLogout} />
            </ProtectedRoute>
          }
        />
        
        <Route
          path="/ai-business-plan/:studentId"
          element={
            <ProtectedRoute allowedRoles={["educator", "admin"]}>
              <AIBusinessPlanPage user={user} onLogout={handleLogout} />
            </ProtectedRoute>
          }
        />

        {/* Pitch Practice (LemonBot scored) */}
        <Route
          path="/pitch-practice"
          element={
            <ProtectedRoute allowedRoles={["student", "educator", "admin"]}>
              <PitchPracticePage user={user} onLogout={handleLogout} />
            </ProtectedRoute>
          }
        />

        {/* Educator: Student Preview Mode (LemonBot dry-run, no logging) */}
        <Route
          path="/educator/student-preview"
          element={
            <ProtectedRoute allowedRoles={["educator", "admin"]}>
              <StudentPreviewPage user={user} onLogout={handleLogout} />
            </ProtectedRoute>
          }
        />

        {/* Educator: View as Student (read-only impersonation + fresh demo) */}
        <Route
          path="/educator/view-as-student"
          element={
            <ProtectedRoute allowedRoles={["educator", "admin"]}>
              <ViewAsStudentPage user={user} onLogout={handleLogout} />
            </ProtectedRoute>
          }
        />
        
        <Route
          path="/admin"
          element={
            <ProtectedRoute allowedRoles={["educator", "admin"]}>
              <AdminDashboard user={user} onLogout={handleLogout} />
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin/announcements"
          element={
            <ProtectedRoute allowedRoles={["admin"]}>
              <AdminAnnouncementsPage user={user} onLogout={handleLogout} />
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin/leads"
          element={
            <ProtectedRoute allowedRoles={["admin"]}>
              <AdminLeadsPage user={user} onLogout={handleLogout} />
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin/demo-reports"
          element={
            <ProtectedRoute allowedRoles={["admin"]}>
              <AdminDemoReportsPage user={user} onLogout={handleLogout} />
            </ProtectedRoute>
          }
        />

        {/* CEO Challenge — Youth CEO Camp Final Challenge */}
        <Route
          path="/ceo-challenge"
          element={
            <ProtectedRoute>
              <CEOChallengeStart user={user} />
            </ProtectedRoute>
          }
        />
        <Route
          path="/ceo-challenge/play"
          element={
            <ProtectedRoute>
              <CEOChallengePlay />
            </ProtectedRoute>
          }
        />
        <Route
          path="/ceo-challenge/results/:attemptId"
          element={
            <ProtectedRoute>
              <CEOChallengeResults />
            </ProtectedRoute>
          }
        />
        <Route
          path="/ceo-challenge/leaderboard"
          element={
            <ProtectedRoute>
              <CEOChallengeLeaderboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/ceo-challenge"
          element={
            <ProtectedRoute allowedRoles={["admin", "educator"]}>
              <AdminCEOChallenge />
            </ProtectedRoute>
          }
        />
      </Routes>
      <Footer />
      {user && <AccessibilityFloatingButton />}
    </BrowserRouter>
  );
}

function App() {
  // Fire once very early: capture marketing UTM params + init PostHog if keyed.
  // Both are idempotent and fail silently when prerequisites are missing.
  useEffect(() => {
    try { captureAttribution(); } catch { /* ignore */ }
    try { initAnalytics(); } catch { /* ignore */ }
  }, []);

  return (
    <ErrorBoundary scope="root">
      <AccessibilityProvider>
        <AppShell />
      </AccessibilityProvider>
    </ErrorBoundary>
  );
}

export default App;
